import { useRef, useState } from 'react'
import { ArrowDown, ArrowUp, Check, Pencil, Plus, Trash2 } from 'lucide-react'
import { useClickOutside } from '@/lib/useClickOutside'
import { cn } from '@/lib/cn'
import type { StageInput } from '@/api/pipelines'
import type { PipelineDetail, Section, Stage, StageType } from '@/api/types'
import { usePipelineDetail, usePipelines } from '@/features/board/useBoard'
import Select from '@/components/ui/Select'
import { useFieldDefinitions } from '@/features/contacts/useContacts'
import { requirableFields, requiredLabels } from '@/lib/stageRequirements'
import ConfirmButton from '@/components/ui/ConfirmButton'
import { apiErrorMessage } from './apiErrorMessage'
import {
  stageToInput,
  useCreatePipeline,
  useCreateSection,
  useCreateStage,
  useDeletePipeline,
  useDeleteSection,
  useDeleteStage,
  useReorderSections,
  useReorderStages,
  useUpdatePipeline,
  useUpdateSection,
  useUpdateStage,
} from './usePipelineAdmin'

const STAGE_TYPES: StageType[] = ['active', 'limbo', 'won', 'lost']

const TYPE_BADGE: Record<StageType, string> = {
  active: 'bg-gray-100 text-gray-600',
  limbo: 'bg-brand-accent/20 text-brand-dark',
  won: 'bg-success/15 text-success',
  lost: 'bg-danger/15 text-danger',
}

const FALLBACK_COLOR = '#94a3b8'

const inputCls =
  'px-2 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/40'
const iconBtnCls = 'text-gray-400 hover:text-brand-primary disabled:opacity-30 disabled:hover:text-gray-400'

/** Every mutation reports into the tab-level error banner (and clears it on success). */
interface Notify {
  onError: (e: unknown) => void
  onSuccess: () => void
}

function withSuccess(notify: Notify, fn: () => void): Notify {
  return {
    onError: notify.onError,
    onSuccess: () => {
      notify.onSuccess()
      fn()
    },
  }
}

function stagesIn(pipeline: PipelineDetail, sectionId: string | null): Stage[] {
  if (sectionId === null) return pipeline.looseStages
  return pipeline.sections.find((s) => s.id === sectionId)?.stages ?? []
}

function nextPos(stages: Stage[]): number {
  return stages.reduce((m, s) => Math.max(m, s.position), -1) + 1
}

function StageEditor({
  stage,
  pipeline,
  pending,
  onSave,
  onCancel,
}: {
  stage: Stage
  pipeline: PipelineDetail
  pending: boolean
  onSave: (input: StageInput) => void
  onCancel: () => void
}) {
  const [draft, setDraft] = useState({
    name: stage.name,
    shortName: stage.shortName ?? '',
    color: stage.color ?? FALLBACK_COLOR,
    stageType: stage.stageType,
    sectionId: stage.sectionId ?? '',
    autoAdvance: stage.autoAdvance,
    advanceAfterDays: stage.advanceAfterDays == null ? '' : String(stage.advanceAfterDays),
    advanceOnNextContact: stage.advanceOnNextContact,
    requiredFields: stage.requiredFields ?? [],
  })
  const { data: dealFields } = useFieldDefinitions('deal')
  const requirable = requirableFields(dealFields ?? [])
  const toggleRequired = (key: string) =>
    setDraft((d) => ({
      ...d,
      requiredFields: d.requiredFields.includes(key)
        ? d.requiredFields.filter((k) => k !== key)
        : [...d.requiredFields, key],
    }))

  function save() {
    const sectionId = draft.sectionId || null
    const sectionChanged = sectionId !== stage.sectionId
    const parsed = Math.floor(Number(draft.advanceAfterDays))
    const days = draft.advanceAfterDays.trim() !== '' && Number.isFinite(parsed) && parsed >= 0 ? parsed : null
    onSave(
      stageToInput(stage, {
        name: draft.name.trim(),
        shortName: draft.shortName.trim() || null,
        color: draft.color,
        stageType: draft.stageType,
        sectionId,
        // moving into another section appends the stage at its end
        position: sectionChanged ? nextPos(stagesIn(pipeline, sectionId)) : stage.position,
        autoAdvance: draft.autoAdvance,
        advanceAfterDays: days,
        advanceOnNextContact: draft.advanceOnNextContact,
        // keep the admin's tick order, dropping fields deleted since
        requiredFields: draft.requiredFields.filter((k) => requirable.some((r) => r.key === k)),
      }),
    )
  }

  return (
    <div className="px-4 py-3 bg-gray-50/60 border-t border-gray-100 space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <input
          className={`${inputCls} flex-1 min-w-40`}
          placeholder="Stage name"
          value={draft.name}
          onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
        />
        <input
          className={`${inputCls} w-24`}
          placeholder="Short"
          title="Short name for compact views"
          value={draft.shortName}
          onChange={(e) => setDraft((d) => ({ ...d, shortName: e.target.value }))}
        />
        <input
          type="color"
          title="Stage colour"
          className="h-8 w-12 rounded border border-gray-200 bg-white"
          value={draft.color}
          onChange={(e) => setDraft((d) => ({ ...d, color: e.target.value }))}
        />
        <Select
          title="Stage type"
          value={draft.stageType}
          onChange={(e) => setDraft((d) => ({ ...d, stageType: e.target.value as StageType }))}
        >
          {STAGE_TYPES.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </Select>
        <Select
          title="Section"
          value={draft.sectionId}
          onChange={(e) => setDraft((d) => ({ ...d, sectionId: e.target.value }))}
        >
          <option value="">No section</option>
          {pipeline.sections.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </Select>
      </div>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-gray-600">
        <span className="font-medium text-gray-500">Follow-up:</span>
        <label className="flex items-center gap-1.5">
          flag as stuck after
          <input
            type="number"
            min={0}
            className={`${inputCls} w-16`}
            value={draft.advanceAfterDays}
            onChange={(e) => setDraft((d) => ({ ...d, advanceAfterDays: e.target.value }))}
          />
          days in stage
        </label>
        <label className="flex items-center gap-1.5">
          <input
            type="checkbox"
            checked={draft.advanceOnNextContact}
            onChange={(e) => setDraft((d) => ({ ...d, advanceOnNextContact: e.target.checked }))}
          />
          flag when the next-contact date arrives
        </label>
        <label className="flex items-center gap-1.5">
          <input
            type="checkbox"
            checked={draft.autoAdvance}
            onChange={(e) => setDraft((d) => ({ ...d, autoAdvance: e.target.checked }))}
          />
          auto-advance
        </label>
      </div>

      <div className="flex flex-wrap items-center gap-1.5 text-xs text-gray-600">
          <span className="font-medium text-gray-500 mr-1" title="A person moving a deal here must fill these in first">
            Required to enter:
          </span>
          {requirable.map((r) => {
            const on = draft.requiredFields.includes(r.key)
            return (
              <button
                key={r.key}
                type="button"
                aria-pressed={on}
                onClick={() => toggleRequired(r.key)}
                className={cn(
                  'px-2 py-0.5 rounded-full border',
                  on
                    ? 'bg-brand-primary/10 border-brand-primary/40 text-brand-primary'
                    : 'border-gray-200 text-gray-500 hover:border-gray-300',
                )}
              >
                {on && <Check size={11} className="inline -mt-0.5 mr-0.5" />}
                {r.label}
              </button>
            )
          })}
        </div>

      <div className="flex items-center gap-2 pt-1">
        <button
          type="button"
          disabled={pending || !draft.name.trim()}
          onClick={save}
          className="px-3 py-1.5 bg-brand-primary text-white rounded-lg text-xs font-medium hover:bg-brand-dark disabled:opacity-60"
        >
          {pending ? 'Saving…' : 'Save stage'}
        </button>
        <button type="button" onClick={onCancel} className="px-3 py-1.5 text-xs text-gray-500 hover:text-brand-dark">
          Cancel
        </button>
      </div>
    </div>
  )
}

function StageRow({
  stage,
  pipeline,
  notify,
  onMoveUp,
  onMoveDown,
}: {
  stage: Stage
  pipeline: PipelineDetail
  notify: Notify
  onMoveUp?: () => void
  onMoveDown?: () => void
}) {
  const [editing, setEditing] = useState(false)
  const update = useUpdateStage(pipeline.id)
  const del = useDeleteStage(pipeline.id)
  const { data: dealFields } = useFieldDefinitions('deal')

  if (editing) {
    return (
      <StageEditor
        stage={stage}
        pipeline={pipeline}
        pending={update.isPending}
        onSave={(input) =>
          update.mutate({ id: stage.id, input }, withSuccess(notify, () => setEditing(false)))
        }
        onCancel={() => setEditing(false)}
      />
    )
  }

  const followUp = [
    stage.advanceAfterDays != null ? `flags after ${stage.advanceAfterDays}d in stage` : null,
    stage.advanceOnNextContact ? 'flags on next-contact date' : null,
    stage.autoAdvance ? 'auto-advance' : null,
    stage.requiredFields?.length ? `requires ${requiredLabels(stage.requiredFields, dealFields ?? [])}` : null,
  ]
    .filter(Boolean)
    .join(' · ')

  return (
    <div className="px-4 py-2.5 border-t border-gray-100 flex items-center gap-3">
      <span
        className="w-3 h-3 rounded-full shrink-0"
        style={{ backgroundColor: stage.color ?? FALLBACK_COLOR }}
      />
      <div className="min-w-0 flex-1">
        <span className="text-sm text-brand-dark">{stage.name}</span>
        {stage.shortName && <span className="ml-2 text-xs text-gray-400">{stage.shortName}</span>}
        {followUp && <div className="text-[11px] text-gray-400">{followUp}</div>}
      </div>
      <span className={`text-xs rounded-full px-2 py-0.5 shrink-0 ${TYPE_BADGE[stage.stageType]}`}>
        {stage.stageType}
      </span>
      <button disabled={!onMoveUp} onClick={onMoveUp} className={iconBtnCls} title="Move up">
        <ArrowUp size={14} />
      </button>
      <button disabled={!onMoveDown} onClick={onMoveDown} className={iconBtnCls} title="Move down">
        <ArrowDown size={14} />
      </button>
      <button onClick={() => setEditing(true)} className={iconBtnCls} title="Edit stage">
        <Pencil size={14} />
      </button>
      <ConfirmButton
        onConfirm={() => del.mutate(stage.id, notify)}
        title="Delete stage — deals must be moved out of it first"
      >
        <Trash2 size={14} />
      </ConfirmButton>
    </div>
  )
}

function StageList({
  stages,
  pipeline,
  notify,
}: {
  stages: Stage[]
  pipeline: PipelineDetail
  notify: Notify
}) {
  const reorder = useReorderStages(pipeline.id)

  // Swap the two rows, then renumber the whole container so intra-section order is unambiguous.
  function move(i: number, dir: -1 | 1) {
    const j = i + dir
    if (j < 0 || j >= stages.length) return
    const arr = [...stages]
    ;[arr[i], arr[j]] = [arr[j], arr[i]]
    reorder.mutate(
      arr.map((s, idx) => ({ id: s.id, input: stageToInput(s, { position: idx }) })),
      notify,
    )
  }

  if (stages.length === 0) {
    return <p className="px-4 py-2 text-xs text-gray-400 border-t border-gray-100">No stages here yet.</p>
  }
  return (
    <div>
      {stages.map((s, i) => (
        <StageRow
          key={s.id}
          stage={s}
          pipeline={pipeline}
          notify={notify}
          onMoveUp={i > 0 ? () => move(i, -1) : undefined}
          onMoveDown={i < stages.length - 1 ? () => move(i, 1) : undefined}
        />
      ))}
    </div>
  )
}

function SectionBlock({
  section,
  pipeline,
  notify,
  onMoveUp,
  onMoveDown,
}: {
  section: Section
  pipeline: PipelineDetail
  notify: Notify
  onMoveUp?: () => void
  onMoveDown?: () => void
}) {
  const update = useUpdateSection(pipeline.id)
  const del = useDeleteSection(pipeline.id)
  const [editing, setEditing] = useState(false)
  const [name, setName] = useState(section.name)

  // Save the group's board colour immediately (a picker in the header, not hidden behind rename).
  function saveColor(color: string | null) {
    update.mutate({ id: section.id, input: { name: section.name, position: section.position, color } }, notify)
  }

  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
      <div className="px-4 py-3 flex items-center gap-2 bg-gray-50/40">
        {editing ? (
          <>
            <input
              className={`${inputCls} flex-1`}
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
            />
            <button
              disabled={update.isPending || !name.trim()}
              onClick={() =>
                update.mutate(
                  { id: section.id, input: { name: name.trim(), position: section.position, color: section.color } },
                  withSuccess(notify, () => setEditing(false)),
                )
              }
              className="px-3 py-1.5 bg-brand-primary text-white rounded-lg text-xs font-medium hover:bg-brand-dark disabled:opacity-60"
            >
              {update.isPending ? 'Saving…' : 'Save'}
            </button>
            <button onClick={() => setEditing(false)} className="px-2 py-1.5 text-xs text-gray-500 hover:text-brand-dark">
              Cancel
            </button>
          </>
        ) : (
          <>
            <div className="flex-1 min-w-0 flex items-center gap-2">
              {/* Group colour: click the swatch for a preset palette, custom picker, or clear. */}
              <GroupColorPicker color={section.color} onChange={saveColor} />
              <span className="text-sm font-semibold text-brand-darkest truncate">{section.name}</span>
              <span className="text-xs text-gray-400 shrink-0">
                {section.stages.length} stage{section.stages.length === 1 ? '' : 's'}
              </span>
            </div>
            <button disabled={!onMoveUp} onClick={onMoveUp} className={iconBtnCls} title="Move section up">
              <ArrowUp size={14} />
            </button>
            <button disabled={!onMoveDown} onClick={onMoveDown} className={iconBtnCls} title="Move section down">
              <ArrowDown size={14} />
            </button>
            <button
              onClick={() => {
                setName(section.name)
                setEditing(true)
              }}
              className={iconBtnCls}
              title="Rename section"
            >
              <Pencil size={14} />
            </button>
            <ConfirmButton
              onConfirm={() => del.mutate(section.id, notify)}
              title="Delete section — its stages move to “No section”"
            >
              <Trash2 size={14} />
            </ConfirmButton>
          </>
        )}
      </div>
      <StageList stages={section.stages} pipeline={pipeline} notify={notify} />
    </div>
  )
}

function PipelineNameForm({ pipeline, notify }: { pipeline: PipelineDetail; notify: Notify }) {
  const update = useUpdatePipeline(pipeline.id)
  const [name, setName] = useState(pipeline.name)

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4 flex items-center gap-2">
      <label className="text-xs font-medium text-gray-500 shrink-0">Pipeline name</label>
      <input className={`${inputCls} flex-1`} value={name} onChange={(e) => setName(e.target.value)} />
      <button
        disabled={update.isPending || !name.trim() || name.trim() === pipeline.name}
        onClick={() => update.mutate({ name: name.trim(), isDefault: pipeline.isDefault }, notify)}
        className="px-3 py-1.5 bg-brand-primary text-white rounded-lg text-xs font-medium hover:bg-brand-dark disabled:opacity-60"
      >
        {update.isPending ? 'Saving…' : 'Rename'}
      </button>
    </div>
  )
}

function AddForms({ pipeline, notify }: { pipeline: PipelineDetail; notify: Notify }) {
  const createSection = useCreateSection(pipeline.id)
  const createStage = useCreateStage(pipeline.id)
  const [sectionName, setSectionName] = useState('')
  const [stageName, setStageName] = useState('')
  const [stageSection, setStageSection] = useState(pipeline.sections[0]?.id ?? '')
  const [stageType, setStageType] = useState<StageType>('active')

  function addSection() {
    createSection.mutate(
      { name: sectionName.trim(), position: pipeline.sections.length },
      withSuccess(notify, () => setSectionName('')),
    )
  }

  function addStage() {
    const sectionId = stageSection || null
    createStage.mutate(
      {
        name: stageName.trim(),
        sectionId,
        position: nextPos(stagesIn(pipeline, sectionId)),
        stageType,
      },
      withSuccess(notify, () => setStageName('')),
    )
  }

  return (
    <div className="grid sm:grid-cols-2 gap-4">
      <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-2">
        <div className="text-xs font-semibold text-brand-darkest">New section</div>
        <input
          className={`${inputCls} w-full`}
          placeholder="Section name"
          value={sectionName}
          onChange={(e) => setSectionName(e.target.value)}
        />
        <button
          disabled={createSection.isPending || !sectionName.trim()}
          onClick={addSection}
          className="px-3 py-1.5 bg-brand-primary text-white rounded-lg text-xs font-medium hover:bg-brand-dark disabled:opacity-60 flex items-center gap-1"
        >
          <Plus size={12} /> {createSection.isPending ? 'Adding…' : 'Add section'}
        </button>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-2">
        <div className="text-xs font-semibold text-brand-darkest">New stage</div>
        <div className="flex gap-2">
          <input
            className={`${inputCls} flex-1`}
            placeholder="Stage name"
            value={stageName}
            onChange={(e) => setStageName(e.target.value)}
          />
          <Select
            title="Stage type"
            value={stageType}
            onChange={(e) => setStageType(e.target.value as StageType)}
          >
            {STAGE_TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </Select>
          <Select value={stageSection} onChange={(e) => setStageSection(e.target.value)}>
            <option value="">No section</option>
            {pipeline.sections.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </Select>
        </div>
        <button
          disabled={createStage.isPending || !stageName.trim()}
          onClick={addStage}
          className="px-3 py-1.5 bg-brand-primary text-white rounded-lg text-xs font-medium hover:bg-brand-dark disabled:opacity-60 flex items-center gap-1"
        >
          <Plus size={12} /> {createStage.isPending ? 'Adding…' : 'Add stage'}
        </button>
      </div>
    </div>
  )
}

export default function PipelineTab() {
  const pipelinesQuery = usePipelines()
  const pipelines = pipelinesQuery.data
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const pipelineId = selectedId ?? pipelines?.find((p) => p.isDefault)?.id ?? pipelines?.[0]?.id
  const detailQuery = usePipelineDetail(pipelineId)
  const pipeline = detailQuery.data

  // Never leave the tab on an endless «Loading pipeline…» — surface the failure with a retry.
  const loadError = pipelinesQuery.error ?? detailQuery.error

  function retryLoad() {
    if (pipelinesQuery.isError) void pipelinesQuery.refetch()
    if (detailQuery.isError && pipelineId) void detailQuery.refetch()
  }

  const [error, setError] = useState<string | null>(null)
  const notify: Notify = {
    onError: (e) => setError(apiErrorMessage(e)),
    onSuccess: () => setError(null),
  }

  const reorderSections = useReorderSections(pipelineId)
  const createPl = useCreatePipeline()
  const deletePl = useDeletePipeline()
  const [creatingPipeline, setCreatingPipeline] = useState(false)
  const [newPipelineName, setNewPipelineName] = useState('')
  const current = pipelines?.find((p) => p.id === pipelineId)

  function addPipeline() {
    createPl.mutate(
      { name: newPipelineName.trim() },
      {
        onError: notify.onError,
        onSuccess: (created) => {
          notify.onSuccess()
          setCreatingPipeline(false)
          setNewPipelineName('')
          setSelectedId(created.id) // jump straight into the new (empty) pipeline
        },
      },
    )
  }

  function removePipeline() {
    if (!pipelineId || !pipelines) return
    const idToDelete = pipelineId
    // Switch to another pipeline BEFORE the mutation: once the delete succeeds its caches
    // are dropped, and an observer still on the deleted id would refetch into a 404 flash.
    const next =
      pipelines.find((p) => p.id !== idToDelete && p.isDefault) ??
      pipelines.find((p) => p.id !== idToDelete)
    setSelectedId(next?.id ?? null)
    deletePl.mutate(idToDelete, {
      onSuccess: notify.onSuccess,
      onError: (e) => {
        setSelectedId(idToDelete) // the pipeline is still there — restore the selection
        notify.onError(e)
      },
    })
  }

  function moveSection(i: number, dir: -1 | 1) {
    if (!pipeline) return
    const j = i + dir
    if (j < 0 || j >= pipeline.sections.length) return
    const arr = [...pipeline.sections]
    ;[arr[i], arr[j]] = [arr[j], arr[i]]
    reorderSections.mutate(
      arr.map((s, idx) => ({ id: s.id, input: { name: s.name, position: idx } })),
      notify,
    )
  }

  return (
    <div className="max-w-5xl space-y-4">
      <p className="text-sm text-gray-500">
        Sections group the board; stages are the columns deals move through. Follow-up settings feed the
        automatic reminder ladder.
      </p>

      {error && (
        <div className="flex items-center justify-between bg-danger/10 border border-danger/30 text-danger text-xs rounded-lg px-3 py-2">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="ml-3 font-medium shrink-0">
            dismiss
          </button>
        </div>
      )}

      {pipelines && (
        <div className="flex flex-wrap items-center gap-2">
          {pipelines.length > 1 && (
            <Select value={pipelineId ?? ''} onChange={(e) => setSelectedId(e.target.value)}>
              {pipelines.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                  {p.isDefault ? ' (default)' : ''}
                </option>
              ))}
            </Select>
          )}
          {creatingPipeline ? (
            <>
              <input
                className={inputCls}
                placeholder="New pipeline name"
                value={newPipelineName}
                autoFocus
                onChange={(e) => setNewPipelineName(e.target.value)}
              />
              <button
                disabled={createPl.isPending || !newPipelineName.trim()}
                onClick={addPipeline}
                className="px-3 py-1.5 bg-brand-primary text-white rounded-lg text-xs font-medium hover:bg-brand-dark disabled:opacity-60"
              >
                {createPl.isPending ? 'Creating…' : 'Create'}
              </button>
              <button
                onClick={() => {
                  setCreatingPipeline(false)
                  setNewPipelineName('')
                }}
                className="px-2 py-1.5 text-xs text-gray-500 hover:text-brand-dark"
              >
                Cancel
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => setCreatingPipeline(true)}
                className="px-3 py-1.5 bg-white border border-gray-200 rounded-lg text-xs font-medium text-brand-dark hover:border-brand-primary flex items-center gap-1"
              >
                <Plus size={12} /> New pipeline
              </button>
              {pipelines.length > 1 && pipelineId && (
                <ConfirmButton
                  onConfirm={removePipeline}
                  disabled={deletePl.isPending}
                  confirmLabel={current?.isDefault ? 'Delete default pipeline?' : 'Delete pipeline?'}
                  title={
                    (current?.isDefault
                      ? 'This is the default pipeline — deleting it leaves the workspace without a default. '
                      : '') +
                    'Delete this pipeline — its sections and stages go with it; a pipeline that still has deals cannot be deleted'
                  }
                  className="ml-auto"
                >
                  <Trash2 size={14} />
                </ConfirmButton>
              )}
            </>
          )}
        </div>
      )}

      {loadError ? (
        <div className="bg-white border border-gray-200 rounded-xl p-6 text-center space-y-3">
          <p className="text-sm text-danger">{apiErrorMessage(loadError)}</p>
          <button
            onClick={retryLoad}
            className="px-3 py-1.5 bg-brand-primary text-white rounded-lg text-xs font-medium hover:bg-brand-dark"
          >
            Retry
          </button>
        </div>
      ) : pipelines && pipelines.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-xl p-6 text-center">
          <p className="text-sm text-gray-500">No pipelines in this workspace yet.</p>
          <p className="text-xs text-gray-400 mt-1">Use «New pipeline» above to create one.</p>
        </div>
      ) : !pipeline ? (
        <p className="text-sm text-gray-400">Loading pipeline…</p>
      ) : (
        <>
          <PipelineNameForm key={pipeline.id} pipeline={pipeline} notify={notify} />

          {pipeline.sections.length === 0 && pipeline.looseStages.length === 0 && (
            <div className="bg-white border border-gray-200 rounded-xl p-6 text-center text-sm text-gray-400">
              This pipeline has no stages yet — add a section and stages below.
            </div>
          )}

          {pipeline.sections.map((sec, i) => (
            <SectionBlock
              key={sec.id}
              section={sec}
              pipeline={pipeline}
              notify={notify}
              onMoveUp={i > 0 ? () => moveSection(i, -1) : undefined}
              onMoveDown={i < pipeline.sections.length - 1 ? () => moveSection(i, 1) : undefined}
            />
          ))}

          {pipeline.looseStages.length > 0 && (
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
              <div className="px-4 py-3 bg-gray-50/40">
                <span className="text-sm font-semibold text-brand-darkest">No section</span>
                <span className="ml-2 text-xs text-gray-400">stages outside any section</span>
              </div>
              <StageList stages={pipeline.looseStages} pipeline={pipeline} notify={notify} />
            </div>
          )}

          <AddForms key={`add-${pipeline.id}`} pipeline={pipeline} notify={notify} />
        </>
      )}
    </div>
  )
}

/**
 * A curated palette of soft group colours (matches the board's tinted-header look). The board tints
 * each group at low alpha, so these mid-saturation hues read well both as the dot and as the tint.
 */
const GROUP_COLOR_PRESETS: { name: string; hex: string }[] = [
  { name: 'Sky', hex: '#0EA5E9' },
  { name: 'Blue', hex: '#3B82F6' },
  { name: 'Indigo', hex: '#6366F1' },
  { name: 'Violet', hex: '#8B5CF6' },
  { name: 'Teal', hex: '#14B8A6' },
  { name: 'Emerald', hex: '#10B981' },
  { name: 'Amber', hex: '#F59E0B' },
  { name: 'Orange', hex: '#F97316' },
  { name: 'Rose', hex: '#F43F5E' },
  { name: 'Slate', hex: '#64748B' },
]

/** Swatch + popover to set a group's board colour: pick a preset, choose a custom colour, or clear. */
function GroupColorPicker({ color, onChange }: { color: string | null; onChange: (color: string | null) => void }) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  useClickOutside(ref, () => setOpen(false), open)

  return (
    <div className="relative shrink-0" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        title="Group colour on the board"
        className="block w-4 h-4 rounded-full border border-gray-300 hover:ring-2 hover:ring-brand-primary/30"
        style={
          color
            ? { backgroundColor: color }
            : { backgroundImage: 'repeating-linear-gradient(45deg,#e5e7eb,#e5e7eb 2px,#fff 2px,#fff 4px)' }
        }
      />
      {open && (
        <div className="absolute left-0 top-full z-30 mt-1 w-48 bg-white border border-gray-200 rounded-xl shadow-lg p-2.5">
          <div className="grid grid-cols-5 gap-1.5">
            {GROUP_COLOR_PRESETS.map((p) => (
              <button
                key={p.hex}
                type="button"
                onClick={() => { onChange(p.hex); setOpen(false) }}
                title={p.name}
                aria-label={p.name}
                className={cn(
                  'relative w-6 h-6 rounded-full border border-black/5 flex items-center justify-center hover:scale-110 transition-transform',
                  color?.toLowerCase() === p.hex.toLowerCase() && 'ring-2 ring-offset-1 ring-brand-dark',
                )}
                style={{ backgroundColor: p.hex }}
              >
                {color?.toLowerCase() === p.hex.toLowerCase() && <Check size={13} className="text-white" />}
              </button>
            ))}
          </div>
          <div className="flex items-center justify-between mt-2.5 pt-2 border-t border-gray-100">
            <label className="inline-flex items-center gap-1.5 text-xs text-brand-primary hover:text-brand-dark cursor-pointer">
              <span
                className="w-3.5 h-3.5 rounded-full border border-gray-300"
                style={{ backgroundImage: 'conic-gradient(#f43f5e,#f59e0b,#10b981,#0ea5e9,#8b5cf6,#f43f5e)' }}
              />
              Custom…
              <input
                type="color"
                value={color || '#0EA5E9'}
                onChange={(e) => { onChange(e.target.value); setOpen(false) }}
                className="sr-only"
                aria-label="Custom group colour"
              />
            </label>
            <button
              type="button"
              onClick={() => { onChange(null); setOpen(false) }}
              disabled={!color}
              className="text-xs text-gray-500 hover:text-danger disabled:opacity-40"
            >
              None
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
